import type { AccessibleChannels, Channel } from '@/api/hooks/useChannels';

/**
 * The channel list the header switcher renders. A restricted user
 * (`access.unrestricted === false`) sees ONLY their accessible channels;
 * everyone else (unrestricted, or `access` still loading/failed) sees the full
 * active channel list from `useChannels()`. The dropdown keeps its hardcoded
 * `All` item ONLY for unrestricted users — a restricted user has no `All`
 * option (see {@link showAllChannelsOption}); their view is always one of their
 * own channels.
 */
export function resolveChannelList(
  access: AccessibleChannels | undefined,
  channels: Channel[] | undefined,
): Channel[] | undefined {
  return access?.unrestricted === false ? access.channels : channels;
}

/**
 * Whether the header dropdown shows the hardcoded `All` item. Only unrestricted
 * users get `All`; a restricted user's view is always one of their own channels
 * — a user assigned a single channel sees just that channel and nothing else.
 */
export function showAllChannelsOption(
  access: AccessibleChannels | undefined,
): boolean {
  return access?.unrestricted !== false;
}

/**
 * Decide the next persisted channel selection, or `null` for "leave it".
 *
 * v4 restricted-user reconciliation (only when `access` is DEFINED and
 * `!unrestricted` — so a transient fetch failure never resets a valid pick):
 * a restricted user is NEVER left on `ALL` and never on a channel outside their
 * set. Any such selection is coerced to their per-warehouse default lens, or —
 * when no default is set — their first accessible channel (so a single-channel
 * user simply lands on their one channel). Only `null` (leave it) when the
 * current selection is already one of their channels.
 *
 * Otherwise (unrestricted, or `access` not yet resolved) it falls back to the
 * v3 selected-channel-deleted reconcile: a persisted slug that matches no
 * active channel reverts to `ALL`. `channels === undefined` while fetching, so
 * a transient failure there can't clear a valid selection either.
 */
export function reconcileChannelSelection(
  access: AccessibleChannels | undefined,
  channels: Channel[] | undefined,
  selectedChannel: string,
): string | null {
  if (access && !access.unrestricted) {
    const inSet = access.channels.some((c) => c.slug === selectedChannel);
    if (inSet) return null;
    // `ALL` or an out-of-set slug → default lens, else first accessible channel.
    return access.defaultSlug ?? access.channels[0]?.slug ?? null;
  }

  // v3 fallback (unrestricted / access unresolved).
  if (!channels) return null;
  if (selectedChannel === 'ALL') return null;
  if (!channels.some((c) => c.slug === selectedChannel)) return 'ALL';
  return null;
}
